import type { FxRenderPacket } from "./fxRuntime";
import { mergeFxHybridDefaults } from "./fxHybridComposition";
import { ghostTrailBlurGpuModule } from "./fxGpuModules.ghostTrail";

export type FxGpuModuleId =
  | "gpu.lightLeak"
  | "gpu.lightLeakSweep"
  | "gpu.lightLeakBurst"
  | "gpu.filmBurn"
  | "gpu.ghostTrailBlur"
  | "gpu.halation"
  | "gpu.contrastBloom"
  | "gpu.dreamGlow"
  | "gpu.anamorphicStreak"
  | "gpu.bloomThreshold"
  | "gpu.softFilmFade"
  | "gpu.bleachBypass"
  | "gpu.kodakLutPack"
  | "gpu.filmGrain"
  | "gpu.dust"
  | "gpu.lensDust"
  | "gpu.dustOverlay"
  | "gpu.sparkleDust"
  | "gpu.haze"
  | "gpu.chromaticAberration"
  | "gpu.chromeGlow"
  | "gpu.chromaticShift"
  | "gpu.bokehFlares"
  | "gpu.fogRoll"
  | "gpu.smokeDrift"
  | "gpu.fogDepth"
  | "gpu.rainStreaks"
  | "gpu.snow"
  | "gpu.floatingEmbers"
  | "gpu.waterDroplets"
  | "gpu.heatDistortion"
  | "gpu.confettiBurst"
  | "gpu.fireSparks"
  | "gpu.scratches"
  | "gpu.vignette"
  | "gpu.glitchRgb"
  | "gpu.glitchTear"
  | "gpu.ripple"
  | "gpu.rippleWarp"
  | "gpu.tempoRipple"
  | "gpu.motionBlurX"
  | "gpu.motionBlurY"
  | "gpu.dataMosh"
  | "gpu.screenCrack"
  | "gpu.speedRampBlur"
  | "gpu.flashWhitePro"
  | "gpu.directionalMotionBlur"
  | "gpu.barrel"
  | "gpu.twirl"
  | "gpu.scanline"
  | "gpu.scanlineJitter"
  | "gpu.beatScanline"
  | "gpu.beatRgbPulse"
  | "gpu.beatParticleBurst"
  | "gpu.beatLightLeak"
  | "gpu.barFlash"
  | "gpu.trebleSparkle"
  | "gpu.impactFlash"
  | "gpu.shockwave"
  | "gpu.impactShockwave";

export type FxGpuModuleKind = "replace" | "overlay";

export type FxGpuUniforms = {
  intensity: number;
  progress: number;
  seed: number;
  time: number;
  amplitude: number;
  speed: number;
  opacity: number;
  resolution: [number, number];
};

export type FxGpuModule = {
  id: FxGpuModuleId;
  label: string;
  kind: FxGpuModuleKind;
  webglFragment: string;
  webgpuFragment?: string;
  resolveUniforms: (args: {
    packet: FxRenderPacket;
    nowMs: number;
    width: number;
    height: number;
  }) => FxGpuUniforms;
};

const clamp01 = (value: number): number => Math.max(0, Math.min(1, value));
const clamp = (value: number, min: number, max: number): number => Math.max(min, Math.min(max, value));
const toFinite = (value: number | undefined, fallback: number): number => (Number.isFinite(value) ? Number(value) : fallback);

function resolveFilterUniformTriplet(args: {
  packet: FxRenderPacket;
  params?: Record<string, number>;
  intensity: number;
  amplitude: number;
  speed: number;
}): { intensity: number; amplitude: number; speed: number } {
  const { packet } = args;
  const params = args.params ?? packet.params ?? {};
  const effectType = String(packet.effectType || "").trim().toLowerCase();
  let intensity = args.intensity;
  let amplitude = args.amplitude;
  let speed = args.speed;

  if (effectType === "chromatic-aberration-soft") {
    intensity = toFinite(params.amount, intensity);
    amplitude = 0.35 + toFinite(params.radialFalloff, 0.72) * 1.65;
    speed = 0.6 + toFinite(params.edgeStrength, 0.78) * 1.8;
  } else if (effectType === "halation-glow") {
    const threshold = clamp(toFinite(params.threshold, 0.66), 0.3, 0.98);
    intensity = clamp(intensity * (0.88 + (1 - threshold) * 0.32), 0, 2);
    amplitude = 0.42 + toFinite(params.radius, 0.46) * 2.02;
    speed = 0.48 + toFinite(params.softKnee, 0.34) * 1.62;
  } else if (effectType === "dream-glow") {
    const threshold = clamp(toFinite(params.threshold, 0.61), 0.3, 0.98);
    intensity = clamp(intensity * (0.94 + (1 - threshold) * 0.4), 0, 2);
    amplitude = 0.54 + toFinite(params.radius, 0.58) * 2.36;
    speed = 0.62 + toFinite(params.softKnee, 0.52) * 2;
  } else if (effectType === "anamorphic-streak") {
    const threshold = clamp(toFinite(params.threshold, 0.9), 0.4, 0.99);
    intensity = clamp(intensity * (1 + (1 - threshold) * 0.45), 0, 2);
    amplitude = 0.5 + toFinite(params.streakLength, 0.78) * 2.4;
    speed = 0.35 + toFinite(params.thickness, 0.08) * 2.6;
  } else if (
    effectType === "cinematic-bloom-threshold" ||
    effectType === "contrast-bloom"
  ) {
    const threshold = clamp(toFinite(params.threshold, 0.66), 0.3, 0.98);
    intensity = clamp(intensity * (1 + (1 - threshold) * 0.35), 0, 2);
    amplitude = 0.45 + toFinite(params.radius, 0.45) * 2.3;
    speed = 0.55 + toFinite(params.softKnee, 0.34) * 1.8;
  } else if (effectType === "chrome-glow") {
    const threshold = clamp(toFinite(params.threshold, 0.58), 0.3, 0.98);
    intensity = clamp(intensity * (0.9 + (1 - threshold) * 0.46), 0, 2);
    amplitude = 0.45 + toFinite(params.radius, 0.56) * 1.95;
    speed = 0.45 + toFinite(params.softKnee, 0.42) * 1.9;
  } else if (effectType === "cinematic-haze") {
    const hazeDensity = clamp(toFinite(params.hazeDensity, 0.62), 0, 1);
    const hazeTint = clamp(toFinite(params.hazeTint, 0.08), -1, 1);
    intensity = clamp(intensity * (0.54 + hazeDensity * 0.58), 0, 2);
    amplitude = 0.34 + hazeDensity * 1.62;
    speed = 0.5 + ((hazeTint + 1) * 0.5) * 1.15;
  } else if (effectType === "light-leak-sweep") {
    intensity = clamp(intensity * (0.95 + toFinite(params.highlightInteraction, 0.55) * 0.7), 0, 2);
    amplitude = 0.4 + toFinite(params.width, 0.48) * 2.2;
    speed = 0.5 + toFinite(params.feather, 0.54) * 1.6;
  } else if (effectType === "film-burn") {
    intensity = clamp(intensity * (0.95 + toFinite(params.textureScale, 0.62) * 0.5), 0, 2);
    amplitude = 0.45 + toFinite(params.textureScale, 0.62) * 2.1;
    speed = 0.5 + toFinite(params.feather, 0.45) * 1.8;
  } else if (effectType === "film-grain") {
    intensity = clamp(intensity * (0.78 + toFinite(params.softness, 0.34) * 0.42), 0, 2);
    amplitude = 0.35 + toFinite(params.grainSize, 0.52) * 2.2;
    speed = 0.7 + toFinite(params.softness, 0.34) * 1.6;
  } else if (effectType === "lens-dust") {
    intensity = toFinite(params.opacity, intensity);
    amplitude = 0.45 + toFinite(params.density, 0.28) * 2.4;
    speed = 0.45 + toFinite(params.driftSpeed, 0.58) * 2.1;
  } else if (effectType === "bleach-bypass") {
    intensity = toFinite(params.amount, intensity);
    amplitude = 0.5 + toFinite(params.contrast, 0.62) * 2.0;
    speed = 0.5 + toFinite(params.saturation, 0.34) * 1.4;
  } else if (effectType === "kodak-lut-pack") {
    intensity = toFinite(params.strength, intensity);
    amplitude = 0.5 + toFinite(params.contrast, 0.26) * 1.8;
    speed = 1 + toFinite(params.exposure, 0.08) * 1.8;
  } else if (effectType === "soft-film-fade") {
    const lift = clamp(toFinite(params.lift, 0.24), 0, 1);
    const highlightRolloff = clamp(toFinite(params.highlightRolloff, 0.44), 0, 1);
    intensity = clamp(intensity * (0.72 + lift * 0.42 + highlightRolloff * 0.18), 0, 2);
    amplitude = 0.45 + toFinite(params.vignetteStrength, 0.3) * 2.0;
    speed = 0.4 + toFinite(params.fadeSoftness, 0.52) * 1.9;
  } else if (effectType === "screen-crack-impact") {
    intensity = clamp(intensity * (0.86 + toFinite(params.crackDensity, 0.84) * 0.48), 0, 2);
    amplitude = 0.4 + toFinite(params.radialDistortion, 0.46) * 2.6;
    speed = 0.45 + toFinite(params.vibrationStrength, 0.62) * 1.8;
  } else if (effectType === "shockwave" || effectType === "impact-shockwave") {
    const ring = toFinite(params.ringDistortion, effectType === "impact-shockwave" ? 0.82 : 0.74);
    const motionBlur = toFinite(params.motionBlurStrength, effectType === "impact-shockwave" ? 0.66 : 0.58);
    intensity = clamp(intensity * (0.88 + ring * 0.4), 0, 2);
    amplitude = 0.5 + ring * 2.3;
    speed = 0.48 + motionBlur * 1.95;
  }

  return {
    intensity,
    amplitude,
    speed,
  };
}

function resolveFilterProgressSeedOverrides(args: {
  packet: FxRenderPacket;
  params?: Record<string, number>;
}): {
  progress?: number;
  seed?: number;
} {
  const { packet } = args;
  const params = packet.params ?? {};
  const resolvedParams = args.params ?? params;
  const effectType = String(packet.effectType || "").trim().toLowerCase();

  if (effectType === "chromatic-aberration-soft") {
    return {
      progress: clamp(toFinite(resolvedParams.centerX, 0.5), 0, 1),
      seed: clamp(toFinite(resolvedParams.centerY, 0.5), 0, 1),
    };
  }

  if (effectType === "film-burn") {
    return {
      progress: clamp(toFinite(resolvedParams.position, 0.5), 0, 1),
      seed: clamp((toFinite(resolvedParams.clamp, 1.25) - 0.4) / 1.6, 0, 1),
    };
  }

  if (effectType === "light-leak-sweep") {
    return {
      progress: clamp(toFinite(resolvedParams.angle, 35) / 180, 0, 1),
      seed: clamp(toFinite(resolvedParams.tintHue, 30) / 360, 0, 1),
    };
  }

  if (effectType === "kodak-lut-pack") {
    return {
      progress: clamp(toFinite(resolvedParams.kodakPreset, 0) / 2, 0, 1),
      seed: clamp(toFinite(resolvedParams.strength, 0.68), 0, 1),
    };
  }

  if (
    effectType === "halation-glow" ||
    effectType === "cinematic-bloom-threshold" ||
    effectType === "contrast-bloom" ||
    effectType === "dream-glow" ||
    effectType === "chrome-glow"
  ) {
    const thresholdNorm = clamp((toFinite(resolvedParams.threshold, 0.66) - 0.3) / 0.68, 0, 1);
    const debugView = clamp(Math.round(toFinite(resolvedParams.debugView, 0)), 0, 2);
    return {
      progress: clamp((toFinite(resolvedParams.clamp, 1.2) - 0.4) / 1.6, 0, 1),
      // Pack threshold + debug mode into one uniform slot for glow modules.
      // seed = thresholdNorm + debugView * 2.0, where debugView: 0=final,1=glowSrc,2=glowBlur
      seed: thresholdNorm + debugView * 2,
    };
  }

  if (effectType === "soft-film-fade") {
    return {
      progress: clamp(toFinite(resolvedParams.lift, 0.24), 0, 1),
      seed: clamp(toFinite(resolvedParams.highlightRolloff, 0.44), 0, 1),
    };
  }
  if (effectType === "film-grain") {
    const monochrome = clamp(toFinite(resolvedParams.monochrome, 1), 0, 1);
    const seedNoise = Math.abs(toFinite(packet.seed, 0) * 0.61803398875) % 1;
    return {
      progress: clamp(toFinite(resolvedParams.lumaBias, 0.52), 0, 1),
      seed: monochrome * 2 + seedNoise,
    };
  }

  if (
    effectType === "screen-crack-impact" ||
    effectType === "shockwave" ||
    effectType === "impact-shockwave"
  ) {
    return {
      progress: clamp(toFinite(resolvedParams.impactCenterX, 0.5), 0, 1),
      seed: clamp(toFinite(resolvedParams.impactCenterY, 0.5), 0, 1),
    };
  }
  return {};
}

const resolveUniformsBase = (args: {
  packet: FxRenderPacket;
  nowMs: number;
  width: number;
  height: number;
}): FxGpuUniforms => {
  const { packet, nowMs, width, height } = args;
  const params = mergeFxHybridDefaults(packet.effectType, packet.params ?? {});
  const resolved = resolveFilterUniformTriplet({
    packet,
    params,
    intensity: toFinite(params.intensity, 1),
    amplitude: toFinite(params.amplitude, 1),
    speed: toFinite(params.speed, 1),
  });
  const overrides = resolveFilterProgressSeedOverrides({ packet, params });
  return {
    intensity: clamp01(resolved.intensity),
    progress: clamp01(Number.isFinite(overrides.progress) ? Number(overrides.progress) : packet.progress ?? 0),
    seed: Number.isFinite(overrides.seed) ? Number(overrides.seed) : Number.isFinite(packet.seed) ? packet.seed : 0,
    time: nowMs / 1000,
    amplitude: clamp(resolved.amplitude, 0, 3),
    speed: clamp(resolved.speed, 0.1, 4),
    opacity: clamp01(packet.opacity ?? 1),
    resolution: [Math.max(1, width), Math.max(1, height)],
  };
};

const WEBGL_HEADER = `
precision mediump float;
varying vec2 v_uv;
uniform sampler2D u_texture;
uniform vec2 u_resolution;
uniform float u_time;
uniform float u_intensity;
uniform float u_progress;
uniform float u_seed;
uniform float u_amp;
uniform float u_speed;
float rand(vec2 co) {
  return fract(sin(dot(co.xy ,vec2(12.9898,78.233))) * 43758.5453 + u_seed);
}
vec3 toLinear(vec3 srgb) {
  return pow(max(srgb, vec3(0.0)), vec3(2.2));
}
vec3 toSrgb(vec3 linear) {
  return pow(max(linear, vec3(0.0)), vec3(0.45454545));
}
float lumaLinear(vec3 linear) {
  return dot(linear, vec3(0.2126, 0.7152, 0.0722));
}
float highlightMask(float luminance, float threshold, float knee) {
  float k = max(0.0005, knee);
  return smoothstep(threshold - k, threshold + k, luminance);
}
vec3 hueToRgb(float h) {
  vec3 rgb = clamp(abs(mod(h * 6.0 + vec3(0.0, 4.0, 2.0), 6.0) - 3.0) - 1.0, 0.0, 1.0);
  return rgb * rgb * (3.0 - 2.0 * rgb);
}
`;

const WEBGL_FOOTER = `
`;

const WEBGPU_HEADER = `
struct Uniforms {
  resolution: vec2<f32>,
  time: f32,
  intensity: f32,
  progress: f32,
  seed: f32,
  amp: f32,
  speed: f32,
};
@group(0) @binding(0) var u_texture: texture_2d<f32>;
@group(0) @binding(1) var u_sampler: sampler;
@group(0) @binding(2) var<uniform> uniforms: Uniforms;
fn rand(co: vec2<f32>) -> f32 {
  return fract(sin(dot(co, vec2<f32>(12.9898,78.233))) * 43758.5453 + uniforms.seed);
}
`;

const WEBGPU_FOOTER = `
`;

const moduleDefs: FxGpuModule[] = [
  {
    id: "gpu.lightLeak",
    label: "Light Leak",
    kind: "overlay",
    webglFragment: `${WEBGL_HEADER}
void main() {
  vec4 base = texture2D(u_texture, v_uv);
  float sweep = sin(u_time * 0.6 * u_speed + v_uv.y * 3.14 + u_progress * 3.0);
  float leak = smoothstep(0.1, 1.0, sweep) * smoothstep(1.0, 0.1, v_uv.x);
  vec3 leakColor = mix(vec3(1.0,0.6,0.2), vec3(1.0,0.3,0.1), v_uv.y);
  vec3 color = mix(base.rgb, leakColor, leak * u_intensity * 0.8);
  gl_FragColor = vec4(color, base.a);
}
${WEBGL_FOOTER}`,
    webgpuFragment: `${WEBGPU_HEADER}
@fragment
fn main(@builtin(position) position: vec4<f32>) -> @location(0) vec4<f32> {
  let uv = position.xy / uniforms.resolution;
  let base = textureSample(u_texture, u_sampler, uv);
  let sweep = sin(uniforms.time * 0.6 * uniforms.speed + uv.y * 3.14 + uniforms.progress * 3.0);
  let leak = smoothstep(0.1, 1.0, sweep) * smoothstep(1.0, 0.1, uv.x);
  let leakColor = mix(vec3<f32>(1.0,0.6,0.2), vec3<f32>(1.0,0.3,0.1), uv.y);
  let color = mix(base.rgb, leakColor, leak * uniforms.intensity * 0.8);
  return vec4<f32>(color, base.a);
}
${WEBGPU_FOOTER}`,
    resolveUniforms: resolveUniformsBase,
  },
  {
    id: "gpu.lightLeakSweep",
    label: "Light Leak Sweep",
    kind: "overlay",
    webglFragment: `${WEBGL_HEADER}
void main() {
  vec4 base = texture2D(u_texture, v_uv);
  vec3 baseLin = toLinear(base.rgb);
  float angle = clamp(u_progress, 0.0, 1.0) * 3.14159265;
  vec2 dir = normalize(vec2(cos(angle), sin(angle)));
  vec2 normal = vec2(-dir.y, dir.x);
  float tintHue = fract(clamp(u_seed, 0.0, 1.0));
  float widthCtl = clamp(u_amp / 3.0, 0.0, 1.0);
  float softCtl = clamp(u_speed / 4.0, 0.0, 1.0);
  float travel = mix(-0.78, 0.78, fract(u_time * (0.035 + softCtl * 0.19)));
  float dist = dot(v_uv - vec2(0.5), normal) - travel;
  float coreW = 0.035 + widthCtl * 0.16;
  float haloW = coreW + 0.08 + softCtl * 0.24;
  float core = exp(-pow(dist / max(coreW, 0.0001), 2.0));
  float halo = exp(-pow(dist / max(haloW, 0.0001), 2.0));
  float along = abs(dot(v_uv - vec2(0.5), dir));
  float alongMask = smoothstep(0.92, 0.12, along);
  float grain = rand(floor(v_uv * vec2(140.0, 90.0) + u_time * vec2(18.0, 6.0)));
  float shimmer = 0.85 + grain * 0.42;
  vec3 tintA = mix(vec3(1.0, 0.66, 0.28), hueToRgb(tintHue), 0.64);
  vec3 tintB = mix(vec3(1.0, 0.42, 0.16), hueToRgb(tintHue + 0.08), 0.74);
  vec3 leakColor = tintA * (0.34 + core * 0.66) + tintB * halo * 0.86;
  float leakLum = dot(leakColor, vec3(0.2126, 0.7152, 0.0722));
  leakColor = mix(vec3(leakLum), leakColor, 0.74);
  float lum = lumaLinear(baseLin);
  float highlightGate = smoothstep(0.12, 0.9, lum);
  float leakAlpha = (core * 1.05 + halo * 0.82) * (0.42 + 1.45 * u_intensity) * (0.54 + alongMask * 0.46) * shimmer;
  leakAlpha *= mix(0.86, 1.18, highlightGate);
  vec3 glow = leakColor * leakAlpha;
  vec3 addComp = baseLin + glow * 0.92;
  vec3 screenComp = 1.0 - (1.0 - baseLin) * (1.0 - glow * 1.15);
  vec3 outLin = mix(screenComp, addComp, 0.36);
  outLin += leakColor * halo * (0.04 + 0.09 * u_intensity);
  outLin = outLin / (vec3(1.0) + outLin / 2.05);
  gl_FragColor = vec4(toSrgb(outLin), base.a);
}
${WEBGL_FOOTER}`,
    resolveUniforms: resolveUniformsBase,
  },
  {
    id: "gpu.lightLeakBurst",
    label: "Light Leak Burst",
    kind: "overlay",
    webglFragment: `${WEBGL_HEADER}
void main() {
  vec4 base = texture2D(u_texture, v_uv);
  vec2 p = v_uv - vec2(0.5, 0.5);
  float radial = smoothstep(0.66, 0.0, length(p));
  float pulse = 0.5 + 0.5 * sin(u_time * 3.2 * u_speed + u_progress * 6.0);
  vec3 leak = mix(vec3(1.0, 0.55, 0.2), vec3(1.0, 0.85, 0.55), pulse);
  vec3 color = mix(base.rgb, base.rgb + leak, radial * u_intensity * 0.58);
  gl_FragColor = vec4(color, base.a);
}
${WEBGL_FOOTER}`,
    resolveUniforms: resolveUniformsBase,
  },
  {
    id: "gpu.filmBurn",
    label: "Film Burn",
    kind: "overlay",
    webglFragment: `${WEBGL_HEADER}
void main() {
  vec4 base = texture2D(u_texture, v_uv);
  vec3 baseLin = toLinear(base.rgb);
  float sweepCenter = clamp(u_progress, 0.0, 1.0);
  float clampCtl = clamp(u_seed, 0.0, 1.0);
  float textureCtl = clamp(u_amp / 3.0, 0.0, 1.0);
  float featherCtl = clamp(u_speed / 4.0, 0.0, 1.0);

  float animatedCenter = sweepCenter + sin(u_time * 0.27) * 0.12;
  float noiseA = rand(v_uv * (24.0 + textureCtl * 56.0) + vec2(u_time * 0.23, -u_time * 0.17));
  float noiseB = rand(v_uv.yx * (16.0 + textureCtl * 42.0) + vec2(-u_time * 0.13, u_time * 0.19));
  float edgeNoise = (noiseA * 0.62 + noiseB * 0.38 - 0.5) * (0.06 + textureCtl * 0.24);
  float dist = v_uv.x - (animatedCenter + edgeNoise);

  float brightWidth = 0.028 + featherCtl * 0.16;
  float haloWidth = brightWidth + 0.06 + featherCtl * 0.22;
  float edgeCore = exp(-pow(dist / max(brightWidth, 0.0001), 2.0));
  float edgeHalo = exp(-pow(dist / max(haloWidth, 0.0001), 2.0));
  float charMask = smoothstep(-0.2 - featherCtl * 0.12, -0.018, dist) * (1.0 - smoothstep(0.01, 0.16 + featherCtl * 0.1, dist));

  vec3 flameDeep = vec3(1.0, 0.34, 0.08);
  vec3 flameHot = vec3(1.0, 0.9, 0.36);
  vec3 ember = vec3(1.0, 0.56, 0.2);
  vec3 flame = mix(flameDeep, flameHot, clamp(edgeCore * 1.2 + noiseA * 0.3, 0.0, 1.0));
  float flameLum = dot(flame, vec3(0.2126, 0.7152, 0.0722));
  flame = mix(vec3(flameLum), flame, 0.72);
  vec3 glow = flame * edgeCore * (0.38 + u_intensity * 1.45) + ember * edgeHalo * (0.16 + u_intensity * 0.82);

  vec3 addComp = baseLin + glow * 0.94;
  vec3 screenComp = 1.0 - (1.0 - baseLin) * (1.0 - glow * 1.2);
  vec3 burnedLin = mix(screenComp, addComp, 0.34);
  burnedLin *= 1.0 - charMask * (0.16 + u_intensity * 0.22);
  burnedLin += vec3(1.0, 0.86, 0.62) * edgeHalo * (0.06 + 0.18 * u_intensity);

  float sparkle = step(0.9975, rand(v_uv * vec2(220.0, 160.0) + vec2(u_time * 1.8, u_time * 1.2)));
  burnedLin += vec3(1.0, 0.78, 0.42) * sparkle * (0.035 + u_intensity * 0.09);

  float tone = mix(1.28, 2.86, clampCtl);
  vec3 outLin = burnedLin / (vec3(1.0) + burnedLin / tone);
  gl_FragColor = vec4(toSrgb(outLin), base.a);
}
${WEBGL_FOOTER}`,
    resolveUniforms: resolveUniformsBase,
  },
  ghostTrailBlurGpuModule as FxGpuModule,
  {
    id: "gpu.halation",
    label: "Halation",
    kind: "overlay",
    webglFragment: `${WEBGL_HEADER}
vec3 extractHalation(vec2 uv, float threshold, float knee) {
  vec3 sampleLin = toLinear(texture2D(u_texture, uv).rgb);
  float lum = lumaLinear(sampleLin);
  float m = highlightMask(lum, threshold, knee);
  float sat = length(sampleLin - vec3(lum));
  float satBoost = smoothstep(0.01, 0.28, sat);
  return sampleLin * m * (0.8 + satBoost * 0.36);
}
void main() {
  vec4 base = texture2D(u_texture, v_uv);
  vec3 baseLin = toLinear(base.rgb);
  vec2 px = vec2(1.0 / u_resolution.x, 1.0 / u_resolution.y);
  float debugMode = floor(clamp(u_seed, 0.0, 9.0) / 2.0 + 0.001);
  float thresholdNorm = clamp(u_seed - debugMode * 2.0, 0.0, 1.0);
  float threshold = mix(0.3, 0.98, thresholdNorm);
  float knee = mix(0.012, 0.18, clamp(u_speed / 4.0, 0.0, 1.0));
  float radius = clamp(u_amp / 3.0, 0.0, 1.0);

  vec3 glowSrc = extractHalation(v_uv, threshold, knee);
  float edge = length(vec2(dFdx(lumaLinear(baseLin)), dFdy(lumaLinear(baseLin))));
  float edgeBoost = smoothstep(0.006, 0.06, edge);

  vec3 blurSmall = vec3(0.0);
  float ws = 0.0;
  float rs = mix(1.2, 5.2, radius);
  for (int i = -4; i <= 4; i++) {
    float fi = float(i);
    float w = exp(-fi * fi * 0.24);
    blurSmall += extractHalation(v_uv + vec2(fi * px.x * rs, 0.0), threshold, knee) * w;
    blurSmall += extractHalation(v_uv + vec2(0.0, fi * px.y * rs * 0.7), threshold, knee) * (w * 0.7);
    ws += w * 1.7;
  }
  blurSmall /= max(ws, 0.0001);

  vec3 blurWide = vec3(0.0);
  float ww = 0.0;
  float rw = mix(3.5, 13.5, radius);
  for (int j = -3; j <= 3; j++) {
    float fj = float(j);
    float w = exp(-fj * fj * 0.22);
    blurWide += extractHalation(v_uv + vec2(fj * px.x * rw, 0.0), threshold, knee) * w;
    blurWide += extractHalation(v_uv + vec2(0.0, fj * px.y * rw * 0.8), threshold, knee) * (w * 0.8);
    ww += w * 1.8;
  }
  blurWide /= max(ww, 0.0001);

  vec3 tint = vec3(1.0, 0.42, 0.2);
  vec3 glowBlur = (blurSmall * 0.52 + blurWide * 0.84) * tint * (0.82 + edgeBoost * 1.18);
  vec3 glowDebug = glowSrc * tint * (0.86 + edgeBoost * 0.92);

  if (debugMode > 1.5) {
    gl_FragColor = vec4(toSrgb(glowBlur), base.a);
    return;
  }
  if (debugMode > 0.5) {
    gl_FragColor = vec4(toSrgb(glowDebug * 2.3), base.a);
    return;
  }

  vec3 glow = glowBlur * (0.16 + 0.72 * u_intensity) * (0.86 + edgeBoost * 0.62);
  vec3 addComp = baseLin + glow;
  vec3 screenComp = 1.0 - (1.0 - baseLin) * (1.0 - glow * 0.86);
  vec3 outLin = mix(screenComp, addComp, 0.54);
  outLin = mix(baseLin, outLin, 0.72 + edgeBoost * 0.22);
  float clampMix = clamp(u_progress, 0.0, 1.0);
  float tone = mix(1.78, 1.06, clampMix);
  outLin = outLin / (vec3(1.0) + outLin / tone);
  gl_FragColor = vec4(toSrgb(outLin), base.a);
}
${WEBGL_FOOTER}`,
    resolveUniforms: resolveUniformsBase,
  },
  {
    id: "gpu.contrastBloom",
    label: "Contrast Bloom",
    kind: "overlay",
    webglFragment: `${WEBGL_HEADER}
vec3 extractContrastBloom(vec2 uv, float threshold, float knee) {
  vec3 sampleLin = toLinear(texture2D(u_texture, uv).rgb);
  float m = highlightMask(lumaLinear(sampleLin), threshold, knee);
  return sampleLin * m;
}
void main() {
  vec4 base = texture2D(u_texture, v_uv);
  vec3 baseLin = toLinear(base.rgb);
  vec2 px = vec2(1.0 / u_resolution.x, 1.0 / u_resolution.y);
  float debugMode = floor(clamp(u_seed, 0.0, 9.0) / 2.0 + 0.001);
  float thresholdNorm = clamp(u_seed - debugMode * 2.0, 0.0, 1.0);
  float threshold = mix(0.3, 0.98, thresholdNorm);
  float knee = mix(0.008, 0.16, clamp(u_speed / 4.0, 0.0, 1.0));
  float radius = clamp(u_amp / 3.0, 0.0, 1.0);

  vec3 srcGlow = extractContrastBloom(v_uv, threshold, knee);
  vec3 blurA = vec3(0.0);
  float wA = 0.0;
  float rA = mix(1.4, 5.2, radius);
  for (int i = -4; i <= 4; i++) {
    float fi = float(i);
    float w = exp(-fi * fi * 0.24);
    blurA += extractContrastBloom(v_uv + vec2(fi * px.x * rA, 0.0), threshold, knee) * w;
    blurA += extractContrastBloom(v_uv + vec2(0.0, fi * px.y * rA), threshold, knee) * (w * 0.85);
    wA += w * 1.85;
  }
  blurA /= max(wA, 0.0001);

  vec3 blurB = vec3(0.0);
  float wB = 0.0;
  float rB = mix(3.2, 12.5, radius);
  for (int j = -3; j <= 3; j++) {
    float fj = float(j);
    float w = exp(-fj * fj * 0.2);
    blurB += extractContrastBloom(v_uv + vec2(fj * px.x * rB, 0.0), threshold, knee) * w;
    blurB += extractContrastBloom(v_uv + vec2(0.0, fj * px.y * rB), threshold, knee) * w;
    wB += w * 2.0;
  }
  blurB /= max(wB, 0.0001);

  vec3 glowBlur = blurA * 0.72 + blurB * 0.86;
  if (debugMode > 1.5) {
    gl_FragColor = vec4(toSrgb(glowBlur), base.a);
    return;
  }
  if (debugMode > 0.5) {
    gl_FragColor = vec4(toSrgb(srcGlow * 2.3), base.a);
    return;
  }

  vec3 centered = (baseLin - 0.5) * (1.0 + 0.28 + u_intensity * 0.34) + 0.5;
  centered = max(centered, vec3(0.0));
  vec3 glow = glowBlur * (0.2 + 0.9 * u_intensity) * vec3(1.0, 0.98, 0.95);
  vec3 addComp = centered + glow;
  vec3 screenComp = 1.0 - (1.0 - centered) * (1.0 - glow * 0.9);
  vec3 outLin = mix(screenComp, addComp, 0.55);
  float clampMix = clamp(u_progress, 0.0, 1.0);
  float tone = mix(1.9, 1.08, clampMix);
  outLin = outLin / (vec3(1.0) + outLin / tone);
  gl_FragColor = vec4(toSrgb(outLin), base.a);
}
${WEBGL_FOOTER}`,
    resolveUniforms: resolveUniformsBase,
  },
  {
    id: "gpu.dreamGlow",
    label: "Dream Glow",
    kind: "overlay",
    webglFragment: `${WEBGL_HEADER}
vec3 extractDreamGlow(vec2 uv, float threshold, float knee) {
  vec3 sampleLin = toLinear(texture2D(u_texture, uv).rgb);
  float lum = lumaLinear(sampleLin);
  float m = highlightMask(lum, threshold, knee);
  return sampleLin * m;
}
void main() {
  vec4 base = texture2D(u_texture, v_uv);
  vec3 baseLin = toLinear(base.rgb);
  vec2 px = vec2(1.0 / u_resolution.x, 1.0 / u_resolution.y);
  float debugMode = floor(clamp(u_seed, 0.0, 9.0) / 2.0 + 0.001);
  float thresholdNorm = clamp(u_seed - debugMode * 2.0, 0.0, 1.0);
  float threshold = mix(0.3, 0.98, thresholdNorm);
  float knee = mix(0.02, 0.2, clamp(u_speed / 4.0, 0.0, 1.0));
  float radius = clamp(u_amp / 3.0, 0.0, 1.0);
  vec2 center = vec2(0.5, 0.5);
  float radialFalloff = 1.0 - smoothstep(0.28, 0.95, length(v_uv - center));

  vec3 srcGlow = extractDreamGlow(v_uv, threshold, knee) * (0.68 + radialFalloff * 0.52);
  vec3 blurCore = vec3(0.0);
  float wCore = 0.0;
  float rCore = mix(1.6, 6.0, radius);
  for (int i = -4; i <= 4; i++) {
    float fi = float(i);
    float w = exp(-fi * fi * 0.22);
    blurCore += extractDreamGlow(v_uv + vec2(fi * px.x * rCore, 0.0), threshold, knee) * w;
    blurCore += extractDreamGlow(v_uv + vec2(0.0, fi * px.y * rCore), threshold, knee) * w;
    wCore += w * 2.0;
  }
  blurCore /= max(wCore, 0.0001);

  vec3 blurWide = vec3(0.0);
  float wWide = 0.0;
  float rWide = mix(4.0, 16.0, radius);
  for (int j = -3; j <= 3; j++) {
    float fj = float(j);
    float w = exp(-fj * fj * 0.2);
    blurWide += extractDreamGlow(v_uv + vec2(fj * px.x * rWide, 0.0), threshold, knee) * w;
    blurWide += extractDreamGlow(v_uv + vec2(0.0, fj * px.y * rWide), threshold, knee) * (w * 0.9);
    wWide += w * 1.9;
  }
  blurWide /= max(wWide, 0.0001);

  vec3 tint = vec3(1.0, 0.8, 0.92);
  vec3 glowBlur = (blurCore * 0.62 + blurWide * 0.95) * tint;
  if (debugMode > 1.5) {
    gl_FragColor = vec4(toSrgb(glowBlur), base.a);
    return;
  }
  if (debugMode > 0.5) {
    gl_FragColor = vec4(toSrgb(srcGlow * tint * 2.1), base.a);
    return;
  }

  float soften = 0.08 + u_intensity * 0.14;
  vec3 pastel = mix(vec3(lumaLinear(baseLin)), baseLin, 0.82);
  vec3 softened = mix(baseLin, pastel, soften * 0.28);
  float lumaGate = smoothstep(0.1, 0.9, lumaLinear(baseLin));
  vec3 glow = glowBlur * (0.2 + u_intensity * 0.82) * (0.84 + radialFalloff * 0.34) * (0.56 + lumaGate * 0.44);
  vec3 screenComp = 1.0 - (1.0 - softened) * (1.0 - glow * 0.98);
  vec3 addComp = softened + glow * 0.56;
  vec3 outLin = mix(screenComp, addComp, 0.28);
  outLin = mix(baseLin, outLin, 0.8);
  float clampMix = clamp(u_progress, 0.0, 1.0);
  float tone = mix(2.08, 1.16, clampMix);
  outLin = outLin / (vec3(1.0) + outLin / tone);
  gl_FragColor = vec4(toSrgb(outLin), base.a);
}
${WEBGL_FOOTER}`,
    resolveUniforms: resolveUniformsBase,
  },
  {
    id: "gpu.anamorphicStreak",
    label: "Anamorphic Streak",
    kind: "overlay",
    webglFragment: `${WEBGL_HEADER}
float highlightAt(vec2 uv, vec2 px, float threshold) {
  vec3 c0 = texture2D(u_texture, uv).rgb;
  vec3 c1 = texture2D(u_texture, uv + vec2(0.0, px.y * 0.85)).rgb;
  vec3 c2 = texture2D(u_texture, uv - vec2(0.0, px.y * 0.85)).rgb;
  float l0 = dot(c0, vec3(0.299,0.587,0.114));
  float l1 = dot(c1, vec3(0.299,0.587,0.114));
  float l2 = dot(c2, vec3(0.299,0.587,0.114));
  float luma = l0 * 0.64 + l1 * 0.18 + l2 * 0.18;
  return smoothstep(threshold, min(1.0, threshold + 0.045), luma);
}

void main() {
  vec4 base = texture2D(u_texture, v_uv);
  float threshold = mix(0.965, 0.885, clamp(u_intensity, 0.0, 1.0));
  vec2 px = vec2(1.0 / u_resolution.x, 1.0 / u_resolution.y);

  float nearAccum = 0.0;
  float nearWeight = 0.0;
  for (int i = -20; i <= 20; i++) {
    float fi = float(i);
    float w = exp(-abs(fi) / 5.0);
    nearAccum += highlightAt(v_uv + vec2(fi * px.x * 1.25, 0.0), px, threshold) * w;
    nearWeight += w;
  }

  float farAccum = 0.0;
  float farWeight = 0.0;
  for (int j = -12; j <= 12; j++) {
    float fj = float(j);
    float w = exp(-abs(fj) / 3.2);
    farAccum += highlightAt(v_uv + vec2(fj * px.x * 4.8, 0.0), px, threshold) * w;
    farWeight += w;
  }

  float nearStreak = pow(clamp(nearAccum / max(nearWeight, 0.0001), 0.0, 1.0), 1.4);
  float farStreak = pow(clamp(farAccum / max(farWeight, 0.0001), 0.0, 1.0), 1.6);
  float streak = clamp(nearStreak + farStreak * 0.35, 0.0, 1.35);
  float core = highlightAt(v_uv, px, threshold);
  vec3 tint = vec3(0.40, 0.78, 1.0);
  float gain = (0.04 + 0.24 * u_intensity) * (0.8 + 0.2 * core);
  vec3 color = base.rgb + tint * streak * gain;
  gl_FragColor = vec4(color, base.a);
}
${WEBGL_FOOTER}`,
    resolveUniforms: resolveUniformsBase,
  },
  {
    id: "gpu.bloomThreshold",
    label: "Cinematic Bloom Threshold",
    kind: "overlay",
    webglFragment: `${WEBGL_HEADER}
vec3 extractBloom(vec2 uv, float threshold, float knee) {
  vec3 sampleLin = toLinear(texture2D(u_texture, uv).rgb);
  float m = highlightMask(lumaLinear(sampleLin), threshold, knee);
  return sampleLin * m;
}
void main() {
  vec4 base = texture2D(u_texture, v_uv);
  vec3 baseLin = toLinear(base.rgb);
  float debugMode = floor(clamp(u_seed, 0.0, 9.0) / 2.0 + 0.001);
  float thresholdNorm = clamp(u_seed - debugMode * 2.0, 0.0, 1.0);
  float threshold = mix(0.3, 0.98, thresholdNorm);
  float knee = mix(0.008, 0.17, clamp(u_speed / 4.0, 0.0, 1.0));
  float radius = clamp(u_amp / 3.0, 0.0, 1.0);
  vec2 px = vec2(1.0 / u_resolution.x, 1.0 / u_resolution.y);

  vec3 glowSrc = extractBloom(v_uv, threshold, knee);
  vec3 blurNear = vec3(0.0);
  float wNear = 0.0;
  float rNear = mix(1.3, 4.8, radius);
  for (int i = -4; i <= 4; i++) {
    float fi = float(i);
    float w = exp(-fi * fi * 0.24);
    blurNear += extractBloom(v_uv + vec2(fi * px.x * rNear, 0.0), threshold, knee) * w;
    blurNear += extractBloom(v_uv + vec2(0.0, fi * px.y * rNear), threshold, knee) * w;
    wNear += w * 2.0;
  }
  blurNear /= max(wNear, 0.0001);

  vec3 blurMid = vec3(0.0);
  float wMid = 0.0;
  float rMid = mix(3.0, 10.5, radius);
  for (int j = -3; j <= 3; j++) {
    float fj = float(j);
    float w = exp(-fj * fj * 0.22);
    blurMid += extractBloom(v_uv + vec2(fj * px.x * rMid, 0.0), threshold, knee) * w;
    blurMid += extractBloom(v_uv + vec2(0.0, fj * px.y * rMid), threshold, knee) * (w * 0.92);
    wMid += w * 1.92;
  }
  blurMid /= max(wMid, 0.0001);

  vec3 blurFar = vec3(0.0);
  float wFar = 0.0;
  float rFar = mix(5.0, 18.0, radius);
  for (int k = -2; k <= 2; k++) {
    float fk = float(k);
    float w = exp(-fk * fk * 0.16);
    blurFar += extractBloom(v_uv + vec2(fk * px.x * rFar, 0.0), threshold, knee) * w;
    blurFar += extractBloom(v_uv + vec2(0.0, fk * px.y * rFar), threshold, knee) * (w * 0.88);
    wFar += w * 1.88;
  }

  blurFar /= max(wFar, 0.0001);
  vec3 glowBlur = blurNear * 0.5 + blurMid * 0.76 + blurFar * 0.96;

  if (debugMode > 1.5) {
    gl_FragColor = vec4(toSrgb(glowBlur), base.a);
    return;
  }
  if (debugMode > 0.5) {
    gl_FragColor = vec4(toSrgb(glowSrc * 2.4), base.a);
    return;
  }

  vec3 glow = glowBlur * (0.22 + u_intensity * 0.92);
  vec3 addComp = baseLin + glow;
  vec3 screenComp = 1.0 - (1.0 - baseLin) * (1.0 - glow * 0.88);
  vec3 outLin = mix(screenComp, addComp, 0.5);
  float clampMix = clamp(u_progress, 0.0, 1.0);
  float tone = mix(2.1, 1.08, clampMix);
  outLin = outLin / (vec3(1.0) + outLin / tone);
  gl_FragColor = vec4(toSrgb(outLin), base.a);
}
${WEBGL_FOOTER}`,
    resolveUniforms: resolveUniformsBase,
  },
  {
    id: "gpu.softFilmFade",
    label: "Soft Film Fade",
    kind: "overlay",
    webglFragment: `${WEBGL_HEADER}
void main() {
  vec4 base = texture2D(u_texture, v_uv);
  vec3 baseLin = toLinear(base.rgb);
  float lift = clamp(u_progress, 0.0, 1.0);
  float highlightRolloff = clamp(u_seed, 0.0, 1.0);
  float fadeSoftness = clamp(u_speed / 4.0, 0.0, 1.0);
  float vignetteStrength = clamp(u_amp / 3.0, 0.0, 1.0);

  float lum = lumaLinear(baseLin);
  float shadowMask = 1.0 - smoothstep(0.08, 0.62 + fadeSoftness * 0.2, lum);
  vec3 lifted = baseLin + vec3((0.05 + 0.2 * lift) * shadowMask * (0.7 + 0.3 * u_intensity));
  float fadeGamma = mix(1.05, 0.78, clamp((lift * 0.72 + fadeSoftness * 0.28) * u_intensity, 0.0, 1.0));
  lifted = pow(max(lifted, vec3(0.0)), vec3(fadeGamma));

  float tone = mix(2.35, 1.18, highlightRolloff);
  vec3 rolled = lifted / (vec3(1.0) + lifted / tone);

  float sat = mix(1.0, 0.8, clamp(u_intensity * (0.25 + fadeSoftness * 0.45), 0.0, 1.0));
  float gray = dot(rolled, vec3(0.2126, 0.7152, 0.0722));
  vec3 desat = mix(vec3(gray), rolled, sat);
  vec3 warmTint = mix(vec3(1.0), vec3(1.06, 1.015, 0.94), clamp(0.22 + lift * 0.45, 0.0, 1.0));
  vec3 toned = desat * warmTint;

  vec2 p = v_uv - vec2(0.5);
  float radial = length(p * vec2(0.95, 1.08));
  float softEdge = smoothstep(0.26, 0.94, radial);
  float edgeDarken = softEdge * (0.08 + vignetteStrength * 0.34) * (0.8 + 0.2 * fadeSoftness);
  toned *= (1.0 - edgeDarken);

  vec2 grainUv = floor(v_uv * u_resolution.xy * mix(0.22, 0.65, fadeSoftness));
  float grain = (rand(grainUv + vec2(u_time * 0.6, u_seed * 13.7)) - 0.5) * (0.012 + 0.02 * u_intensity);
  vec3 outLin = max(toned + vec3(grain), vec3(0.0));
  gl_FragColor = vec4(toSrgb(outLin), base.a);
}
${WEBGL_FOOTER}`,
    resolveUniforms: resolveUniformsBase,
  },
  {
    id: "gpu.bleachBypass",
    label: "Bleach Bypass",
    kind: "overlay",
    webglFragment: `${WEBGL_HEADER}
void main() {
  vec4 base = texture2D(u_texture, v_uv);
  float gray = dot(base.rgb, vec3(0.299,0.587,0.114));
  float satMix = mix(0.18, 0.74, clamp(u_speed / 4.0, 0.0, 1.0));
  float contrast = mix(1.02, 1.74, clamp(u_amp / 3.0, 0.0, 1.0));
  vec3 desat = mix(base.rgb, vec3(gray), satMix);
  vec3 centered = (desat - 0.5) * contrast + 0.5;
  vec3 boosted = mix(centered, vec3(gray), 0.22);
  vec3 color = mix(base.rgb, clamp(boosted, 0.0, 1.0), u_intensity * 0.85);
  gl_FragColor = vec4(color, base.a);
}
${WEBGL_FOOTER}`,
    resolveUniforms: resolveUniformsBase,
  },
  {
    id: "gpu.kodakLutPack",
    label: "Kodak LUT Pack",
    kind: "overlay",
    webglFragment: `${WEBGL_HEADER}
void main() {
  vec4 base = texture2D(u_texture, v_uv);
  float presetRaw = clamp(u_progress, 0.0, 0.999);
  float preset = floor(presetRaw * 3.0);
  float warmMix = step(preset, 0.5);
  float coolMix = step(0.5, preset) * (1.0 - step(1.5, preset));
  float punchMix = step(1.5, preset);
  vec3 c = base.rgb;
  c.r = pow(c.r, mix(0.95, 0.88, punchMix));
  c.g = pow(c.g, mix(1.0, 0.96, punchMix));
  c.b = pow(c.b, mix(1.06, 1.12, coolMix));
  vec3 warm = vec3(1.08, 1.0, 0.9);
  vec3 cool = vec3(0.94, 1.01, 1.1);
  vec3 punch = vec3(1.1, 1.04, 0.9);
  vec3 look = warm * warmMix + cool * coolMix + punch * punchMix;
  c *= mix(vec3(1.0), look, (0.18 + u_seed * 0.28) * u_intensity);
  float contrast = mix(0.9, 1.8, clamp(u_amp / 3.0, 0.0, 1.0));
  c = (c - 0.5) * contrast + 0.5;
  c += (u_speed - 1.0) * 0.14;
  float luma = dot(c, vec3(0.299,0.587,0.114));
  float satMix = mix(0.18, 0.06, punchMix) * u_intensity;
  c = mix(vec3(luma), c, 1.0 - satMix);
  c = clamp(c, 0.0, 1.0);
  gl_FragColor = vec4(c, base.a);
}
${WEBGL_FOOTER}`,
    resolveUniforms: resolveUniformsBase,
  },
  {
    id: "gpu.chromaticAberration",
    label: "Chromatic Aberration",
    kind: "overlay",
    webglFragment: `${WEBGL_HEADER}
void main() {
  vec2 center = vec2(clamp(u_progress, 0.0, 1.0), clamp(u_seed, 0.0, 1.0));
  vec2 fromCenter = v_uv - center;
  float radial = clamp(length(fromCenter) * 1.4142, 0.0, 1.0);
  float falloffPower = mix(1.0, 3.4, clamp(u_amp / 3.0, 0.0, 1.0));
  float edgeWeight = pow(radial, falloffPower);
  float edgeStrength = mix(0.65, 2.4, clamp(u_speed / 4.0, 0.0, 1.0));
  float shift = (0.0012 + 0.0115 * u_intensity) * edgeWeight * edgeStrength;
  vec2 dir = radial > 0.0001 ? normalize(fromCenter) : vec2(1.0, 0.0);
  vec2 uvR = v_uv + dir * shift;
  vec2 uvB = v_uv - dir * shift;
  vec4 r = texture2D(u_texture, uvR);
  vec4 g = texture2D(u_texture, v_uv);
  vec4 b = texture2D(u_texture, uvB);
  vec3 ca = vec3(r.r, g.g, b.b);
  float blend = clamp(0.3 + u_intensity * 0.62, 0.0, 1.0);
  gl_FragColor = vec4(mix(g.rgb, ca, blend), g.a);
}
${WEBGL_FOOTER}`,
    resolveUniforms: resolveUniformsBase,
  },
  {
    id: "gpu.chromeGlow",
    label: "Chrome Glow",
    kind: "overlay",
    webglFragment: `${WEBGL_HEADER}
vec3 extractChromeGlow(vec2 uv, float threshold, float knee) {
  vec3 sampleLin = toLinear(texture2D(u_texture, uv).rgb);
  float lum = lumaLinear(sampleLin);
  float m = highlightMask(lum, threshold, knee);
  float sat = length(sampleLin - vec3(lum));
  float satBoost = smoothstep(0.02, 0.32, sat);
  return sampleLin * m * (0.8 + satBoost * 0.35);
}
void main() {
  vec4 base = texture2D(u_texture, v_uv);
  vec3 baseLin = toLinear(base.rgb);
  vec2 px = vec2(1.0 / u_resolution.x, 1.0 / u_resolution.y);
  float debugMode = floor(clamp(u_seed, 0.0, 9.0) / 2.0 + 0.001);
  float thresholdNorm = clamp(u_seed - debugMode * 2.0, 0.0, 1.0);
  float threshold = mix(0.3, 0.98, thresholdNorm);
  float knee = mix(0.014, 0.19, clamp(u_speed / 4.0, 0.0, 1.0));
  float radius = clamp(u_amp / 3.0, 0.0, 1.0);

  vec3 srcGlow = extractChromeGlow(v_uv, threshold, knee);
  vec3 blurA = vec3(0.0);
  float wA = 0.0;
  float rA = mix(1.4, 5.4, radius);
  for (int i = -4; i <= 4; i++) {
    float fi = float(i);
    float w = exp(-fi * fi * 0.24);
    blurA += extractChromeGlow(v_uv + vec2(fi * px.x * rA, 0.0), threshold, knee) * w;
    blurA += extractChromeGlow(v_uv + vec2(0.0, fi * px.y * rA * 0.75), threshold, knee) * (w * 0.75);
    wA += w * 1.75;
  }
  blurA /= max(wA, 0.0001);

  vec3 blurB = vec3(0.0);
  float wB = 0.0;
  float rB = mix(3.6, 13.0, radius);
  for (int j = -3; j <= 3; j++) {
    float fj = float(j);
    float w = exp(-fj * fj * 0.21);
    blurB += extractChromeGlow(v_uv + vec2(fj * px.x * rB, 0.0), threshold, knee) * w;
    blurB += extractChromeGlow(v_uv + vec2(0.0, fj * px.y * rB * 0.8), threshold, knee) * (w * 0.8);
    wB += w * 1.8;
  }
  blurB /= max(wB, 0.0001);

  float chromaShift = (0.35 + radius) * (0.7 + u_intensity) * px.x * 2.4;
  vec3 srcR = extractChromeGlow(v_uv + vec2(chromaShift, 0.0), threshold, knee);
  vec3 srcB = extractChromeGlow(v_uv - vec2(chromaShift, 0.0), threshold, knee);
  vec3 splitGlow = vec3(srcR.r, srcGlow.g, srcB.b);
  vec3 glowBlur = (blurA * 0.54 + blurB * 0.84 + splitGlow * 0.42) * vec3(0.72, 0.9, 1.03);

  if (debugMode > 1.5) {
    gl_FragColor = vec4(toSrgb(glowBlur), base.a);
    return;
  }
  if (debugMode > 0.5) {
    gl_FragColor = vec4(toSrgb(splitGlow * vec3(1.4, 1.4, 1.8)), base.a);
    return;
  }

  float l0 = lumaLinear(baseLin);
  float lx1 = lumaLinear(toLinear(texture2D(u_texture, v_uv + vec2(px.x * 1.2, 0.0)).rgb));
  float lx2 = lumaLinear(toLinear(texture2D(u_texture, v_uv - vec2(px.x * 1.2, 0.0)).rgb));
  float ly1 = lumaLinear(toLinear(texture2D(u_texture, v_uv + vec2(0.0, px.y * 1.2)).rgb));
  float ly2 = lumaLinear(toLinear(texture2D(u_texture, v_uv - vec2(0.0, px.y * 1.2)).rgb));
  float edge = abs(l0 - lx1) + abs(l0 - lx2) + abs(l0 - ly1) + abs(l0 - ly2);
  float edgeMask = smoothstep(0.02, 0.18, edge);
  float sheen = 0.5 + 0.5 * sin(v_uv.y * u_resolution.y * 0.075 + u_time * (0.25 + u_speed * 0.28));
  vec3 coolTint = mix(vec3(0.62, 0.78, 0.98), vec3(0.86, 0.94, 1.0), sheen);
  vec3 glow =
    glowBlur * (0.14 + u_intensity * 0.62) * mix(0.88, 1.36, edgeMask) +
    coolTint * glowBlur * (0.06 + u_intensity * 0.12);
  vec3 screenComp = 1.0 - (1.0 - baseLin) * (1.0 - glow * 0.94);
  vec3 addComp = baseLin + glow * 0.74;
  vec3 outLin = mix(screenComp, addComp, 0.34 + edgeMask * 0.14);
  outLin = mix(baseLin, outLin, 0.72 + edgeMask * 0.22);
  float clampMix = clamp(u_progress, 0.0, 1.0);
  float tone = mix(1.85, 1.03, clampMix);
  outLin = outLin / (vec3(1.0) + outLin / tone);
  gl_FragColor = vec4(toSrgb(outLin), base.a);
}
${WEBGL_FOOTER}`,
    resolveUniforms: resolveUniformsBase,
  },
  {
    id: "gpu.chromaticShift",
    label: "Chromatic Shift",
    kind: "replace",
    webglFragment: `${WEBGL_HEADER}
void main() {
  float wobble = sin(u_time * 3.4 * u_speed + v_uv.y * 14.0) * 0.0025 * u_intensity;
  vec2 rUv = v_uv + vec2(wobble, 0.0);
  vec2 bUv = v_uv - vec2(wobble, 0.0);
  vec4 r = texture2D(u_texture, rUv);
  vec4 g = texture2D(u_texture, v_uv);
  vec4 b = texture2D(u_texture, bUv);
  gl_FragColor = vec4(r.r, g.g, b.b, g.a);
}
${WEBGL_FOOTER}`,
    resolveUniforms: resolveUniformsBase,
  },
  {
    id: "gpu.filmGrain",
    label: "Film Grain",
    kind: "overlay",
    webglFragment: `${WEBGL_HEADER}
void main() {
  vec4 base = texture2D(u_texture, v_uv);
  vec3 baseLin = toLinear(base.rgb);
  float sizeCtl = clamp(u_amp / 3.0, 0.0, 1.0);
  float softCtl = clamp(u_speed / 4.0, 0.0, 1.0);
  float lumaBias = clamp(u_progress, 0.0, 1.0);
  float monochrome = step(1.0, u_seed);
  float cell = mix(0.72, 4.35, sizeCtl);
  vec2 grainUv = floor(v_uv * u_resolution.xy / cell);

  float n0 = rand(grainUv + vec2(u_time * (0.45 + softCtl * 0.8), 0.0)) - 0.5;
  float n1 = rand(grainUv + vec2(1.0, 0.0) + vec2(u_time * (0.34 + softCtl * 0.5), 2.0)) - 0.5;
  float n2 = rand(grainUv + vec2(0.0, 1.0) + vec2(u_time * (0.29 + softCtl * 0.42), 5.0)) - 0.5;
  float n3 = rand(grainUv + vec2(1.0, 1.0) + vec2(u_time * (0.38 + softCtl * 0.66), 7.0)) - 0.5;
  float softNoise = (n0 + n1 + n2 + n3) * 0.25;
  float lumNoise = mix(n0, softNoise, 0.22 + softCtl * 0.68);
  float chNoise = rand(grainUv * 1.91 + vec2(u_time * 0.77, u_time * 0.49)) - 0.5;

  float lum = lumaLinear(baseLin);
  float darkWeight = 1.0 - smoothstep(0.14, 0.66, lum);
  float brightWeight = smoothstep(0.34, 0.94, lum);
  float lumaWeight = mix(darkWeight, brightWeight, lumaBias);
  float grainAmt = (0.018 + 0.16 * u_intensity) * mix(0.72, 1.14, lumaWeight);
  vec3 grainColor = mix(vec3(lumNoise, lumNoise + chNoise * 0.12, lumNoise - chNoise * 0.12), vec3(lumNoise), monochrome);
  grainColor += vec3(0.02, 0.01, -0.012) * (1.0 - monochrome) * (0.42 + lumaWeight * 0.58);
  vec3 outLin = max(baseLin + grainColor * grainAmt, vec3(0.0));
  outLin *= vec3(0.986, 0.992, 1.0);
  gl_FragColor = vec4(toSrgb(outLin), base.a);
}
${WEBGL_FOOTER}`,
    resolveUniforms: resolveUniformsBase,
  },
  {
    id: "gpu.dust",
    label: "Dust",
    kind: "overlay",
    webglFragment: `${WEBGL_HEADER}
void main() {
  vec4 base = texture2D(u_texture, v_uv);
  float speck = step(0.997, rand(v_uv * 50.0 + u_time * 1.2));
  vec3 color = mix(base.rgb, vec3(1.0), speck * u_intensity);
  gl_FragColor = vec4(color, base.a);
}
${WEBGL_FOOTER}`,
    resolveUniforms: resolveUniformsBase,
  },
  {
    id: "gpu.lensDust",
    label: "Lens Dust",
    kind: "overlay",
    webglFragment: `${WEBGL_HEADER}
void main() {
  vec4 base = texture2D(u_texture, v_uv);
  float density = mix(0.996, 0.942, clamp(u_amp / 3.0, 0.0, 1.0));
  vec2 drift = vec2(u_time * 0.015 * u_speed, u_time * 0.009 * u_speed);
  vec2 grid = floor((v_uv + drift) * vec2(34.0, 58.0));
  float speck = step(density, rand(grid + vec2(floor(u_time * 0.6), u_seed)));
  float haloScale = mix(12.0, 30.0, clamp(u_speed / 4.0, 0.0, 1.0));
  float halo = smoothstep(0.24, 0.0, length(fract(v_uv * vec2(haloScale, haloScale * 1.4)) - 0.5));
  vec3 dust = vec3(1.0, 0.96, 0.9) * (0.35 + 0.65 * halo);
  vec3 color = mix(base.rgb, base.rgb + dust, speck * u_intensity * 0.34);
  gl_FragColor = vec4(color, base.a);
}
${WEBGL_FOOTER}`,
    resolveUniforms: resolveUniformsBase,
  },
  {
    id: "gpu.dustOverlay",
    label: "Dust Overlay",
    kind: "overlay",
    webglFragment: `${WEBGL_HEADER}
void main() {
  vec4 base = texture2D(u_texture, v_uv);
  float speck = step(0.996, rand(v_uv * 72.0 + floor(u_time * 0.9)));
  vec3 dust = vec3(0.95, 0.92, 0.86);
  vec3 color = mix(base.rgb, base.rgb + dust, speck * u_intensity * 0.22);
  gl_FragColor = vec4(color, base.a);
}
${WEBGL_FOOTER}`,
    resolveUniforms: resolveUniformsBase,
  },
  {
    id: "gpu.sparkleDust",
    label: "Sparkle Dust",
    kind: "overlay",
    webglFragment: `${WEBGL_HEADER}
void main() {
  vec4 base = texture2D(u_texture, v_uv);
  float twinkle = 0.5 + 0.5 * sin(u_time * 7.0 * u_speed + v_uv.x * 40.0 + v_uv.y * 50.0);
  float speck = step(0.997, rand(v_uv * 90.0 + floor(u_time * 2.2)));
  vec3 sparkle = vec3(0.9, 0.95, 1.0) * twinkle;
  vec3 color = mix(base.rgb, base.rgb + sparkle, speck * u_intensity * 0.36);
  gl_FragColor = vec4(color, base.a);
}
${WEBGL_FOOTER}`,
    resolveUniforms: resolveUniformsBase,
  },
  {
    id: "gpu.glitchRgb",
    label: "Glitch RGB",
    kind: "replace",
    webglFragment: `${WEBGL_HEADER}
void main() {
  float jitter = sin(u_time * 3.0 + v_uv.y * 20.0) * 0.01 * u_intensity;
  vec2 uvR = v_uv + vec2(jitter, 0.0);
  vec2 uvB = v_uv - vec2(jitter, 0.0);
  vec4 r = texture2D(u_texture, uvR);
  vec4 g = texture2D(u_texture, v_uv);
  vec4 b = texture2D(u_texture, uvB);
  gl_FragColor = vec4(r.r, g.g, b.b, g.a);
}
${WEBGL_FOOTER}`,
    resolveUniforms: resolveUniformsBase,
  },
  {
    id: "gpu.glitchTear",
    label: "Glitch Frame Tear",
    kind: "replace",
    webglFragment: `${WEBGL_HEADER}
void main() {
  float stripe = floor(v_uv.y * 48.0 + u_time * 2.0 * u_speed);
  float tearNoise = rand(vec2(stripe, floor(u_time * 20.0)));
  float tearBand = step(0.88, tearNoise);
  float tearOffset = (rand(vec2(stripe + 4.0, u_seed)) - 0.5) * 0.12 * u_intensity * tearBand;
  vec2 uv = v_uv + vec2(tearOffset, 0.0);
  float jumpNoise = rand(vec2(floor(u_time * 12.0), stripe + 19.0));
  if (jumpNoise > 0.975) {
    uv.y += (rand(vec2(stripe + 11.0, u_time)) - 0.5) * 0.04 * u_intensity;
  }
  float split = tearBand * 0.004 * (0.5 + u_intensity);
  vec4 r = texture2D(u_texture, uv + vec2(split, 0.0));
  vec4 g = texture2D(u_texture, uv);
  vec4 b = texture2D(u_texture, uv - vec2(split, 0.0));
  gl_FragColor = vec4(r.r, g.g, b.b, g.a);
}
${WEBGL_FOOTER}`,
    resolveUniforms: resolveUniformsBase,
  },
  {
    id: "gpu.ripple",
    label: "Ripple",
    kind: "replace",
    webglFragment: `${WEBGL_HEADER}
void main() {
  vec2 center = vec2(0.5, 0.5);
  vec2 dir = v_uv - center;
  float dist = length(dir);
  float wave = sin(dist * 30.0 - u_time * 3.0 * u_speed) * 0.01 * u_intensity;
  vec2 uv = v_uv + normalize(dir) * wave;
  vec4 base = texture2D(u_texture, uv);
  gl_FragColor = base;
}
${WEBGL_FOOTER}`,
    resolveUniforms: resolveUniformsBase,
  },
  {
    id: "gpu.rippleWarp",
    label: "Ripple Warp",
    kind: "replace",
    webglFragment: `${WEBGL_HEADER}
void main() {
  vec2 center = vec2(0.5, 0.5);
  vec2 dir = v_uv - center;
  float dist = max(length(dir), 0.0001);
  float wave = sin(dist * 34.0 - u_time * 3.6 * u_speed) * 0.012 * u_intensity;
  vec2 uv = v_uv + (dir / dist) * wave;
  gl_FragColor = texture2D(u_texture, uv);
}
${WEBGL_FOOTER}`,
    resolveUniforms: resolveUniformsBase,
  },
  {
    id: "gpu.tempoRipple",
    label: "Tempo Ripple",
    kind: "replace",
    webglFragment: `${WEBGL_HEADER}
void main() {
  vec2 center = vec2(0.5, 0.5);
  vec2 dir = v_uv - center;
  float dist = max(length(dir), 0.0001);
  float beat = 0.5 + 0.5 * sin(u_time * 6.0 * u_speed + u_progress * 7.0);
  float wave = sin(dist * 42.0 - u_time * 4.6 * u_speed) * 0.009 * u_intensity * (0.4 + beat * 0.6);
  vec2 uv = v_uv + (dir / dist) * wave;
  gl_FragColor = texture2D(u_texture, uv);
}
${WEBGL_FOOTER}`,
    resolveUniforms: resolveUniformsBase,
  },
  {
    id: "gpu.motionBlurX",
    label: "Motion Blur X",
    kind: "replace",
    webglFragment: `${WEBGL_HEADER}
void main() {
  float blur = 0.004 * u_intensity * u_amp;
  vec4 sum = vec4(0.0);
  sum += texture2D(u_texture, v_uv + vec2(-blur, 0.0));
  sum += texture2D(u_texture, v_uv + vec2(-blur * 0.5, 0.0));
  sum += texture2D(u_texture, v_uv);
  sum += texture2D(u_texture, v_uv + vec2(blur * 0.5, 0.0));
  sum += texture2D(u_texture, v_uv + vec2(blur, 0.0));
  gl_FragColor = sum / 5.0;
}
${WEBGL_FOOTER}`,
    resolveUniforms: resolveUniformsBase,
  },
  {
    id: "gpu.motionBlurY",
    label: "Motion Blur Y",
    kind: "replace",
    webglFragment: `${WEBGL_HEADER}
void main() {
  float blur = 0.004 * u_intensity * u_amp;
  vec4 sum = vec4(0.0);
  sum += texture2D(u_texture, v_uv + vec2(0.0, -blur));
  sum += texture2D(u_texture, v_uv + vec2(0.0, -blur * 0.5));
  sum += texture2D(u_texture, v_uv);
  sum += texture2D(u_texture, v_uv + vec2(0.0, blur * 0.5));
  sum += texture2D(u_texture, v_uv + vec2(0.0, blur));
  gl_FragColor = sum / 5.0;
}
${WEBGL_FOOTER}`,
    resolveUniforms: resolveUniformsBase,
  },
  {
    id: "gpu.dataMosh",
    label: "Data Mosh",
    kind: "replace",
    webglFragment: `${WEBGL_HEADER}
void main() {
  float blockY = floor(v_uv.y * 36.0 + u_time * 7.0 * u_speed);
  float lineNoise = rand(vec2(blockY, floor(u_time * 18.0)));
  float glitchGate = step(0.82, lineNoise);
  float offset = (rand(vec2(blockY + 7.0, u_seed)) - 0.5) * 0.16 * u_intensity * glitchGate;
  vec2 uv = v_uv + vec2(offset, 0.0);
  uv = floor(uv * vec2(160.0, 260.0)) / vec2(160.0, 260.0);
  vec4 base = texture2D(u_texture, uv);
  float split = 0.003 * (0.5 + u_intensity) * glitchGate;
  vec4 r = texture2D(u_texture, uv + vec2(split, 0.0));
  vec4 b = texture2D(u_texture, uv - vec2(split, 0.0));
  gl_FragColor = vec4(r.r, base.g, b.b, base.a);
}
${WEBGL_FOOTER}`,
    resolveUniforms: resolveUniformsBase,
  },
  {
    id: "gpu.screenCrack",
    label: "Screen Crack Impact",
    kind: "overlay",
    webglFragment: `${WEBGL_HEADER}
void main() {
  vec2 center = vec2(clamp(u_progress, 0.0, 1.0), clamp(u_seed, 0.0, 1.0));
  vec2 p = v_uv - center;
  float dist = max(length(p), 0.0001);
  vec2 dir = p / dist;

  float crackDensity = clamp(0.45 + u_intensity * 0.7, 0.45, 1.2);
  float radialDistort = (0.0015 + 0.012 * u_amp) * (0.45 + u_intensity * 0.65);
  float vibration = (0.0008 + 0.0036 * u_speed * u_intensity) * sin(u_time * 38.0 * u_speed + dist * 120.0);
  vec2 vibrateOffset = vec2(cos(u_time * 52.0 + dist * 9.0), sin(u_time * 47.0 + dist * 11.0)) * vibration;

  float angle = atan(dir.y, dir.x);
  float crackRayA = abs(sin(angle * (12.0 + crackDensity * 4.0) + dist * 52.0 + u_time * 0.25));
  float crackRayB = abs(sin(angle * (20.0 + crackDensity * 8.0) - dist * 76.0 - u_time * 0.4));
  float crackLine = 1.0 - smoothstep(0.16, 0.86, crackRayA * 0.62 + crackRayB * 0.38);
  crackLine *= smoothstep(0.88, 0.05, dist);

  float ringRadius = 0.05 + fract(u_time * (0.24 + u_speed * 0.22)) * 0.2;
  float ring = exp(-pow((dist - ringRadius) / max(0.01 + 0.022 * u_amp, 0.0001), 2.0));

  float branchSeed = rand(floor(vec2(angle * 28.0, dist * 210.0 + u_time * 7.0)));
  float branch = step(0.9 - u_intensity * 0.1, branchSeed) * smoothstep(0.5, 0.03, dist);
  float crackMask = clamp(crackLine + branch * 0.52, 0.0, 1.0);

  float distortAmt = (crackMask * radialDistort + ring * radialDistort * 0.72);
  vec2 uv = v_uv + vibrateOffset + dir * distortAmt;

  float chroma = (0.0006 + 0.0034 * u_intensity) * (0.35 + crackMask * 0.65 + ring * 0.45);
  float rr = texture2D(u_texture, uv + dir * chroma).r;
  float gg = texture2D(u_texture, uv).g;
  float bb = texture2D(u_texture, uv - dir * chroma).b;
  vec3 base = vec3(rr, gg, bb);

  vec2 shardCell = floor((v_uv - center + vec2(0.5)) * vec2(210.0, 210.0));
  float shardNoise = rand(shardCell + floor(vec2(u_time * 23.0, u_time * 17.0)));
  float shard = step(0.992 - u_intensity * 0.05, shardNoise) * (0.22 + ring * 1.2 + crackMask * 0.55);
  vec3 shardColor = mix(vec3(0.85, 0.93, 1.0), vec3(1.0, 0.95, 0.86), rand(shardCell * 0.11));

  float flashCore = exp(-dist * (16.0 - u_intensity * 4.0)) * (0.3 + ring * 0.7);
  float flashPulse = 0.6 + 0.4 * sin(u_time * (24.0 + u_speed * 16.0));
  float flash = clamp(flashCore * flashPulse * (0.08 + u_intensity * 0.56), 0.0, 1.0);

  vec3 color = base;
  color += shardColor * shard * (0.24 + u_intensity * 0.54);
  color = mix(color, color * vec3(0.72, 0.78, 0.9), crackMask * 0.2);
  color = mix(color, vec3(1.0), flash);
  color += vec3(0.96, 0.98, 1.0) * crackMask * (0.04 + u_intensity * 0.12);
  gl_FragColor = vec4(color, texture2D(u_texture, uv).a);
}
${WEBGL_FOOTER}`,
    resolveUniforms: resolveUniformsBase,
  },
  {
    id: "gpu.speedRampBlur",
    label: "Speed Ramp Blur",
    kind: "replace",
    webglFragment: `${WEBGL_HEADER}
void main() {
  float accel = smoothstep(0.0, 0.24, u_progress) * (1.0 - smoothstep(0.68, 1.0, u_progress));
  float blur = 0.010 * u_intensity * (0.35 + accel) * u_amp;
  vec2 dir = normalize(vec2(1.0, 0.22));
  vec4 sum = vec4(0.0);
  sum += texture2D(u_texture, v_uv - dir * blur * 1.6) * 0.14;
  sum += texture2D(u_texture, v_uv - dir * blur * 0.8) * 0.19;
  sum += texture2D(u_texture, v_uv) * 0.34;
  sum += texture2D(u_texture, v_uv + dir * blur * 0.8) * 0.19;
  sum += texture2D(u_texture, v_uv + dir * blur * 1.6) * 0.14;
  gl_FragColor = sum;
}
${WEBGL_FOOTER}`,
    resolveUniforms: resolveUniformsBase,
  },
  {
    id: "gpu.flashWhitePro",
    label: "Flash White Pro",
    kind: "overlay",
    webglFragment: `${WEBGL_HEADER}
void main() {
  vec4 base = texture2D(u_texture, v_uv);
  float flashA = smoothstep(0.0, 0.16, u_progress) * (1.0 - smoothstep(0.16, 0.36, u_progress));
  float flashB = smoothstep(0.6, 0.78, u_progress) * (1.0 - smoothstep(0.78, 1.0, u_progress));
  float pulse = max(flashA, flashB);
  float glow = smoothstep(0.0, 1.0, sin(u_time * 12.0 * u_speed) * 0.5 + 0.5);
  float mixAmt = clamp(pulse * (0.62 + glow * 0.38) * u_intensity, 0.0, 1.0);
  vec3 color = mix(base.rgb, vec3(1.0), mixAmt);
  gl_FragColor = vec4(color, base.a);
}
${WEBGL_FOOTER}`,
    resolveUniforms: resolveUniformsBase,
  },
  {
    id: "gpu.directionalMotionBlur",
    label: "Directional Motion Blur Pro",
    kind: "replace",
    webglFragment: `${WEBGL_HEADER}
void main() {
  float theta = 0.35 + sin(u_time * 0.5) * 0.25;
  vec2 dir = normalize(vec2(cos(theta), sin(theta)));
  float blur = 0.0105 * u_intensity * max(0.45, u_amp);
  vec4 sum = vec4(0.0);
  sum += texture2D(u_texture, v_uv - dir * blur * 2.0) * 0.1;
  sum += texture2D(u_texture, v_uv - dir * blur * 1.3) * 0.14;
  sum += texture2D(u_texture, v_uv - dir * blur * 0.6) * 0.18;
  sum += texture2D(u_texture, v_uv) * 0.2;
  sum += texture2D(u_texture, v_uv + dir * blur * 0.6) * 0.18;
  sum += texture2D(u_texture, v_uv + dir * blur * 1.3) * 0.14;
  sum += texture2D(u_texture, v_uv + dir * blur * 2.0) * 0.1;
  gl_FragColor = sum;
}
${WEBGL_FOOTER}`,
    resolveUniforms: resolveUniformsBase,
  },
  {
    id: "gpu.barrel",
    label: "Barrel Warp",
    kind: "replace",
    webglFragment: `${WEBGL_HEADER}
void main() {
  vec2 centered = v_uv - 0.5;
  float r2 = dot(centered, centered);
  vec2 uv = v_uv + centered * r2 * 0.4 * u_intensity;
  vec4 base = texture2D(u_texture, uv);
  gl_FragColor = base;
}
${WEBGL_FOOTER}`,
    resolveUniforms: resolveUniformsBase,
  },
  {
    id: "gpu.twirl",
    label: "Twirl Warp",
    kind: "replace",
    webglFragment: `${WEBGL_HEADER}
void main() {
  vec2 c = v_uv - 0.5;
  float r = length(c);
  float angle = u_intensity * (1.0 - r) * 3.14;
  float s = sin(angle);
  float co = cos(angle);
  vec2 uv = vec2(c.x * co - c.y * s, c.x * s + c.y * co) + 0.5;
  vec4 base = texture2D(u_texture, uv);
  gl_FragColor = base;
}
${WEBGL_FOOTER}`,
    resolveUniforms: resolveUniformsBase,
  },
  {
    id: "gpu.scanline",
    label: "Scanline",
    kind: "overlay",
    webglFragment: `${WEBGL_HEADER}
void main() {
  vec4 base = texture2D(u_texture, v_uv);
  float scan = sin(v_uv.y * u_resolution.y * 1.5);
  float darken = 1.0 - (0.12 * u_intensity * (0.5 + 0.5 * scan));
  gl_FragColor = vec4(base.rgb * darken, base.a);
}
${WEBGL_FOOTER}`,
    resolveUniforms: resolveUniformsBase,
  },
  {
    id: "gpu.scanlineJitter",
    label: "Scanline Jitter",
    kind: "overlay",
    webglFragment: `${WEBGL_HEADER}
void main() {
  vec4 base = texture2D(u_texture, v_uv);
  float scan = sin(v_uv.y * u_resolution.y * 1.9 + u_time * 5.0);
  float jitter = (rand(vec2(floor(v_uv.y * u_resolution.y * 0.25), floor(u_time * 24.0))) - 0.5) * 0.14 * u_intensity;
  float darken = 1.0 - (0.16 * u_intensity * (0.5 + 0.5 * scan));
  vec3 color = base.rgb * darken + vec3(jitter);
  gl_FragColor = vec4(color, base.a);
}
${WEBGL_FOOTER}`,
    resolveUniforms: resolveUniformsBase,
  },
  {
    id: "gpu.beatScanline",
    label: "Beat Scanline",
    kind: "overlay",
    webglFragment: `${WEBGL_HEADER}
void main() {
  vec4 base = texture2D(u_texture, v_uv);
  float beat = 0.5 + 0.5 * sin(u_time * 8.2 * u_speed + u_progress * 8.0);
  float scan = sin(v_uv.y * u_resolution.y * (1.2 + beat * 0.9));
  float darken = 1.0 - (0.18 * u_intensity * (0.5 + 0.5 * scan) * (0.35 + beat * 0.65));
  gl_FragColor = vec4(base.rgb * darken, base.a);
}
${WEBGL_FOOTER}`,
    resolveUniforms: resolveUniformsBase,
  },
  {
    id: "gpu.vignette",
    label: "Vignette",
    kind: "overlay",
    webglFragment: `${WEBGL_HEADER}
void main() {
  vec4 base = texture2D(u_texture, v_uv);
  vec2 p = v_uv - 0.5;
  float v = smoothstep(0.6, 0.95, length(p));
  vec3 color = mix(base.rgb, base.rgb * 0.65, v * u_intensity);
  gl_FragColor = vec4(color, base.a);
}
${WEBGL_FOOTER}`,
    resolveUniforms: resolveUniformsBase,
  },
  {
    id: "gpu.haze",
    label: "Haze",
    kind: "overlay",
    webglFragment: `${WEBGL_HEADER}
void main() {
  vec4 base = texture2D(u_texture, v_uv);
  vec3 baseLin = toLinear(base.rgb);
  float density = clamp(u_amp / 3.0, 0.0, 1.0);
  float tintMix = clamp((u_speed - 0.6) / 1.8, 0.0, 1.0);
  float luma = lumaLinear(baseLin);
  float depth = pow(clamp(v_uv.y, 0.0, 1.0), mix(1.55, 0.96, density));
  float driftA = sin(v_uv.y * 11.0 + u_time * (0.14 + 0.32 * density) + v_uv.x * 4.4) * 0.5 + 0.5;
  float driftB = sin(v_uv.x * 7.6 - u_time * (0.09 + 0.24 * density)) * 0.5 + 0.5;
  float driftC = sin((v_uv.x + v_uv.y) * 5.2 + u_time * 0.2) * 0.5 + 0.5;
  float field = clamp(depth * 0.52 + driftA * 0.24 + driftB * 0.16 + driftC * 0.08, 0.0, 1.0);
  float shadowProtect = smoothstep(0.1, 0.34, luma);
  float midWeight = smoothstep(0.18, 0.72, luma) * (1.0 - smoothstep(0.72, 0.96, luma));
  float highlightBias = smoothstep(0.32, 0.92, luma);
  float hazeMask =
    field *
    (0.03 + density * 0.2) *
    u_intensity *
    (0.16 + midWeight * 0.38 + highlightBias * 0.56) *
    mix(0.34, 1.0, shadowProtect);
  vec3 hazeTint = mix(vec3(0.68, 0.78, 0.9), vec3(0.93, 0.84, 0.72), tintMix);
  float lift = (0.004 + density * 0.016) * (0.2 + depth * 0.8) * (0.2 + highlightBias * 0.8);
  vec3 lifted = baseLin + vec3(lift);
  vec3 curved = pow(max(lifted, vec3(0.0)), vec3(mix(1.03, 0.97, density * 0.8)));
  vec3 veil = hazeTint * hazeMask;
  vec3 addPass = curved + veil * (0.34 + density * 0.22);
  vec3 screenPass = 1.0 - (1.0 - curved) * (1.0 - veil * (0.55 + density * 0.18));
  vec3 outLin = mix(addPass, screenPass, 0.38);
  outLin = mix(baseLin, outLin, 0.48 + density * 0.24);
  float contrast = 1.0 + (0.04 + density * 0.08) * (midWeight * 0.7 + highlightBias * 0.3);
  outLin = (outLin - 0.5) * contrast + 0.5;
  float tone = 1.0 + density * 0.14;
  outLin = outLin / (vec3(1.0) + max(outLin - vec3(1.0), vec3(0.0)) * tone);
  gl_FragColor = vec4(toSrgb(clamp(outLin, 0.0, 1.0)), base.a);
}
${WEBGL_FOOTER}`,
    resolveUniforms: resolveUniformsBase,
  },
  {
    id: "gpu.fogRoll",
    label: "Fog Roll",
    kind: "overlay",
    webglFragment: `${WEBGL_HEADER}
void main() {
  vec4 base = texture2D(u_texture, v_uv);
  float fog = sin((v_uv.y + u_time * 0.1) * 6.0) * 0.5 + 0.5;
  vec3 fogColor = vec3(0.9, 0.9, 0.95);
  vec3 color = mix(base.rgb, fogColor, fog * u_intensity * 0.25);
  gl_FragColor = vec4(color, base.a);
}
${WEBGL_FOOTER}`,
    resolveUniforms: resolveUniformsBase,
  },
  {
    id: "gpu.smokeDrift",
    label: "Smoke Drift",
    kind: "overlay",
    webglFragment: `${WEBGL_HEADER}
void main() {
  vec4 base = texture2D(u_texture, v_uv);
  float rollA = sin((v_uv.y + u_time * 0.07) * 7.5 + v_uv.x * 2.6);
  float rollB = sin((v_uv.x * 4.2) - (u_time * 0.25 * u_speed));
  float fog = (rollA * 0.5 + 0.5) * 0.58 + (rollB * 0.5 + 0.5) * 0.42;
  vec3 smoke = vec3(0.78, 0.8, 0.84);
  vec3 color = mix(base.rgb, smoke, fog * u_intensity * 0.28);
  gl_FragColor = vec4(color, base.a);
}
${WEBGL_FOOTER}`,
    resolveUniforms: resolveUniformsBase,
  },
  {
    id: "gpu.fogDepth",
    label: "Fog Depth Layer",
    kind: "overlay",
    webglFragment: `${WEBGL_HEADER}
void main() {
  vec4 base = texture2D(u_texture, v_uv);
  float depth = smoothstep(0.25, 1.0, v_uv.y);
  float rolling = 0.5 + 0.5 * sin((v_uv.x * 8.0) + (u_time * 0.6 * u_speed));
  float fog = depth * (0.45 + 0.55 * rolling) * u_intensity;
  vec3 fogColor = mix(vec3(0.85,0.9,0.95), vec3(0.92,0.94,1.0), v_uv.x);
  vec3 color = mix(base.rgb, fogColor, fog * 0.42);
  gl_FragColor = vec4(color, base.a);
}
${WEBGL_FOOTER}`,
    resolveUniforms: resolveUniformsBase,
  },
  {
    id: "gpu.rainStreaks",
    label: "Rain Streaks",
    kind: "overlay",
    webglFragment: `${WEBGL_HEADER}
void main() {
  vec4 base = texture2D(u_texture, v_uv);
  float streak = step(0.985, rand(vec2(v_uv.x * 60.0, v_uv.y * 2.0 + u_time * 2.0)));
  vec3 color = mix(base.rgb, vec3(0.8,0.9,1.0), streak * u_intensity);
  gl_FragColor = vec4(color, base.a);
}
${WEBGL_FOOTER}`,
    resolveUniforms: resolveUniformsBase,
  },
  {
    id: "gpu.snow",
    label: "Snow",
    kind: "overlay",
    webglFragment: `${WEBGL_HEADER}
void main() {
  vec4 base = texture2D(u_texture, v_uv);
  float snow = step(0.996, rand(v_uv * 80.0 + u_time * 0.8));
  vec3 color = mix(base.rgb, vec3(1.0), snow * u_intensity);
  gl_FragColor = vec4(color, base.a);
}
${WEBGL_FOOTER}`,
    resolveUniforms: resolveUniformsBase,
  },
  {
    id: "gpu.floatingEmbers",
    label: "Floating Embers",
    kind: "overlay",
    webglFragment: `${WEBGL_HEADER}
void main() {
  vec4 base = texture2D(u_texture, v_uv);
  vec2 uv = v_uv;
  uv.y -= u_time * 0.12 * u_speed;
  float particle = step(0.995, rand(floor(uv * vec2(120.0, 90.0))));
  float shimmer = 0.6 + 0.4 * sin(u_time * 5.0 + v_uv.y * 25.0);
  vec3 ember = vec3(1.0, 0.56, 0.2) * shimmer;
  vec3 color = mix(base.rgb, base.rgb + ember, particle * u_intensity * 0.58);
  gl_FragColor = vec4(color, base.a);
}
${WEBGL_FOOTER}`,
    resolveUniforms: resolveUniformsBase,
  },
  {
    id: "gpu.waterDroplets",
    label: "Water Droplets",
    kind: "overlay",
    webglFragment: `${WEBGL_HEADER}
void main() {
  vec2 grid = floor(v_uv * vec2(18.0, 32.0));
  vec2 seed = grid + vec2(floor(u_time * 0.8), u_seed);
  vec2 jitter = vec2(rand(seed), rand(seed + 1.37));
  vec2 center = (grid + jitter) / vec2(18.0, 32.0);
  vec2 d = v_uv - center;
  float dist = length(d);
  float drop = smoothstep(0.05, 0.0, dist);
  vec2 refractUV = v_uv + normalize(d + 0.0001) * drop * 0.012 * u_intensity;
  vec4 refracted = texture2D(u_texture, refractUV);
  vec4 base = texture2D(u_texture, v_uv);
  vec3 color = mix(base.rgb, refracted.rgb + vec3(0.03, 0.05, 0.07), drop * 0.48);
  gl_FragColor = vec4(color, base.a);
}
${WEBGL_FOOTER}`,
    resolveUniforms: resolveUniformsBase,
  },
  {
    id: "gpu.heatDistortion",
    label: "Heat Distortion",
    kind: "replace",
    webglFragment: `${WEBGL_HEADER}
void main() {
  float wave = sin(v_uv.y * 44.0 + u_time * 6.0 * u_speed) * 0.006 * u_intensity;
  float wave2 = sin(v_uv.y * 19.0 - u_time * 3.0) * 0.004 * u_intensity;
  vec2 uv = v_uv + vec2(wave + wave2, 0.0);
  vec4 base = texture2D(u_texture, uv);
  gl_FragColor = base;
}
${WEBGL_FOOTER}`,
    resolveUniforms: resolveUniformsBase,
  },
  {
    id: "gpu.confettiBurst",
    label: "Confetti Burst",
    kind: "overlay",
    webglFragment: `${WEBGL_HEADER}
vec3 confettiColor(float t) {
  if (t < 0.2) return vec3(1.0, 0.28, 0.36);
  if (t < 0.4) return vec3(0.2, 0.82, 1.0);
  if (t < 0.6) return vec3(1.0, 0.9, 0.18);
  if (t < 0.8) return vec3(0.38, 1.0, 0.46);
  return vec3(0.92, 0.52, 1.0);
}
void main() {
  vec4 base = texture2D(u_texture, v_uv);
  vec2 uv = v_uv;
  uv.y += u_time * 0.18 * u_speed;
  vec2 cell = floor(uv * vec2(64.0, 96.0));
  float n = rand(cell + vec2(u_seed, floor(u_time * 2.0)));
  float bit = step(0.986, n);
  float hue = rand(cell + 9.17);
  vec3 confetti = confettiColor(hue);
  vec3 color = mix(base.rgb, base.rgb + confetti, bit * u_intensity * 0.75);
  gl_FragColor = vec4(color, base.a);
}
${WEBGL_FOOTER}`,
    resolveUniforms: resolveUniformsBase,
  },
  {
    id: "gpu.fireSparks",
    label: "Fire Sparks",
    kind: "overlay",
    webglFragment: `${WEBGL_HEADER}
void main() {
  vec4 base = texture2D(u_texture, v_uv);
  vec2 uv = v_uv;
  uv.y -= u_time * 0.22 * u_speed;
  vec2 cell = floor(uv * vec2(90.0, 140.0));
  float n = rand(cell + vec2(u_seed, floor(u_time * 4.0)));
  float spark = step(0.992, n);
  float trail = smoothstep(0.04, 0.0, fract(uv.y * 14.0 + u_time * 1.7));
  vec3 sparkColor = mix(vec3(1.0,0.56,0.12), vec3(1.0,0.96,0.65), trail);
  vec3 color = mix(base.rgb, base.rgb + sparkColor, spark * u_intensity * (0.5 + 0.5 * trail));
  gl_FragColor = vec4(color, base.a);
}
${WEBGL_FOOTER}`,
    resolveUniforms: resolveUniformsBase,
  },
  {
    id: "gpu.scratches",
    label: "Scratches",
    kind: "overlay",
    webglFragment: `${WEBGL_HEADER}
void main() {
  vec4 base = texture2D(u_texture, v_uv);
  float scratch = step(0.995, rand(vec2(v_uv.x * 200.0, u_time * 1.3)));
  vec3 color = mix(base.rgb, vec3(1.0), scratch * u_intensity);
  gl_FragColor = vec4(color, base.a);
}
${WEBGL_FOOTER}`,
    resolveUniforms: resolveUniformsBase,
  },
  {
    id: "gpu.bokehFlares",
    label: "Bokeh Flares",
    kind: "overlay",
    webglFragment: `${WEBGL_HEADER}
void main() {
  vec4 base = texture2D(u_texture, v_uv);
  float glow = smoothstep(0.0, 1.0, sin(u_time * 0.6 + v_uv.x * 4.0) * 0.5 + 0.5);
  vec3 flare = vec3(1.0,0.8,0.6) * glow * u_intensity * 0.2;
  gl_FragColor = vec4(base.rgb + flare, base.a);
}
${WEBGL_FOOTER}`,
    resolveUniforms: resolveUniformsBase,
  },
  {
    id: "gpu.beatRgbPulse",
    label: "Beat RGB Pulse",
    kind: "replace",
    webglFragment: `${WEBGL_HEADER}
void main() {
  float pulse = smoothstep(0.0, 1.0, sin(u_time * 7.0 * u_speed + u_progress * 6.0) * 0.5 + 0.5);
  float shift = 0.0045 * u_intensity * pulse;
  vec2 uvR = v_uv + vec2(shift, 0.0);
  vec2 uvB = v_uv - vec2(shift, 0.0);
  vec4 r = texture2D(u_texture, uvR);
  vec4 g = texture2D(u_texture, v_uv);
  vec4 b = texture2D(u_texture, uvB);
  gl_FragColor = vec4(r.r, g.g, b.b, g.a);
}
${WEBGL_FOOTER}`,
    resolveUniforms: resolveUniformsBase,
  },
  {
    id: "gpu.beatParticleBurst",
    label: "Beat Particle Burst",
    kind: "overlay",
    webglFragment: `${WEBGL_HEADER}
vec3 particleColor(float n) {
  if (n < 0.33) return vec3(1.0, 0.95, 0.6);
  if (n < 0.66) return vec3(0.62, 0.88, 1.0);
  return vec3(1.0, 0.62, 0.85);
}
void main() {
  vec4 base = texture2D(u_texture, v_uv);
  float beat = smoothstep(0.0, 1.0, sin(u_time * 8.5 * u_speed + u_progress * 7.0) * 0.5 + 0.5);
  vec2 uv = v_uv - 0.5;
  float dist = length(uv);
  float angle = atan(uv.y, uv.x);
  float ring = smoothstep(0.04, 0.0, abs(dist - (0.1 + beat * 0.5)));
  float burst = step(0.992, rand(vec2(floor(angle * 28.0), floor(u_time * 8.0 + beat * 10.0))));
  float n = rand(vec2(angle * 31.0, beat * 12.0));
  vec3 c = particleColor(n);
  float alpha = (ring * 0.45 + burst * 0.65) * u_intensity;
  vec3 color = mix(base.rgb, base.rgb + c, alpha);
  gl_FragColor = vec4(color, base.a);
}
${WEBGL_FOOTER}`,
    resolveUniforms: resolveUniformsBase,
  },
  {
    id: "gpu.beatLightLeak",
    label: "Beat Light Leak",
    kind: "overlay",
    webglFragment: `${WEBGL_HEADER}
void main() {
  vec4 base = texture2D(u_texture, v_uv);
  float beat = smoothstep(0.0, 1.0, sin(u_time * 7.8 * u_speed + u_progress * 5.0) * 0.5 + 0.5);
  float sweep = sin(u_time * 1.1 + v_uv.y * 5.0 + beat * 1.8);
  float leak = smoothstep(0.1, 1.0, sweep) * smoothstep(1.0, 0.1, v_uv.x);
  vec3 leakColor = mix(vec3(1.0, 0.66, 0.24), vec3(1.0, 0.28, 0.18), v_uv.y);
  vec3 color = mix(base.rgb, leakColor, leak * u_intensity * (0.3 + beat * 0.7));
  gl_FragColor = vec4(color, base.a);
}
${WEBGL_FOOTER}`,
    resolveUniforms: resolveUniformsBase,
  },
  {
    id: "gpu.barFlash",
    label: "Bar Flash Overlay",
    kind: "overlay",
    webglFragment: `${WEBGL_HEADER}
void main() {
  vec4 base = texture2D(u_texture, v_uv);
  float t = fract(u_time * 1.4 * u_speed + u_progress * 0.6);
  float bar = smoothstep(0.1, 0.0, abs(v_uv.y - t));
  float pulse = 0.5 + 0.5 * sin(u_time * 8.0 * u_speed + u_progress * 7.0);
  vec3 flash = mix(vec3(1.0), vec3(0.88,0.94,1.0), v_uv.x);
  vec3 color = mix(base.rgb, flash, bar * pulse * u_intensity * 0.8);
  gl_FragColor = vec4(color, base.a);
}
${WEBGL_FOOTER}`,
    resolveUniforms: resolveUniformsBase,
  },
  {
    id: "gpu.trebleSparkle",
    label: "Treble Sparkle",
    kind: "overlay",
    webglFragment: `${WEBGL_HEADER}
void main() {
  vec4 base = texture2D(u_texture, v_uv);
  float pulse = smoothstep(0.0, 1.0, sin(u_time * 12.0 * u_speed + u_progress * 8.0) * 0.5 + 0.5);
  vec2 cell = floor(v_uv * vec2(120.0, 180.0));
  float twinkle = step(0.996, rand(cell + vec2(floor(u_time * 18.0), u_seed)));
  float blink = 0.5 + 0.5 * sin(u_time * 16.0 + v_uv.x * 40.0 + v_uv.y * 30.0);
  vec3 sparkle = vec3(0.88, 0.94, 1.0) * blink;
  vec3 color = mix(base.rgb, base.rgb + sparkle, twinkle * u_intensity * (0.35 + pulse * 0.65));
  gl_FragColor = vec4(color, base.a);
}
${WEBGL_FOOTER}`,
    resolveUniforms: resolveUniformsBase,
  },
  {
    id: "gpu.impactFlash",
    label: "Impact Flash",
    kind: "overlay",
    webglFragment: `${WEBGL_HEADER}
void main() {
  vec4 base = texture2D(u_texture, v_uv);
  float flash = smoothstep(0.0, 1.0, sin(u_time * 8.0) * 0.5 + 0.5);
  vec3 color = mix(base.rgb, vec3(1.0), flash * u_intensity * 0.5);
  gl_FragColor = vec4(color, base.a);
}
${WEBGL_FOOTER}`,
    resolveUniforms: resolveUniformsBase,
  },
  {
    id: "gpu.shockwave",
    label: "Shockwave",
    kind: "replace",
    webglFragment: `${WEBGL_HEADER}
void main() {
  vec2 center = vec2(clamp(u_progress, 0.0, 1.0), clamp(u_seed, 0.0, 1.0));
  vec2 dirRaw = v_uv - center;
  float dist = max(length(dirRaw), 0.0001);
  vec2 dir = dirRaw / dist;
  float amp = clamp(u_amp / 3.0, 0.0, 1.0);
  float speedCtl = clamp(u_speed / 4.0, 0.0, 1.0);

  float travel = fract(u_time * (0.2 + 0.46 * speedCtl));
  float radius = mix(0.04, 0.84, travel);
  float thickness = 0.012 + amp * 0.05;
  float ring = exp(-pow((dist - radius) / max(thickness, 0.0001), 2.0));
  float ringTail = exp(-pow((dist - (radius - 0.05)) / max(thickness * 1.5, 0.0001), 2.0));

  float distortion = (ring * 1.18 + ringTail * 0.62) * (0.004 + u_intensity * 0.032) * (0.55 + amp * 0.95);
  vec2 waveUv = v_uv + dir * distortion;

  float blurSpan = (0.001 + amp * 0.012) * (0.3 + ring * 1.05);
  vec3 blur = vec3(0.0);
  blur += texture2D(u_texture, waveUv - dir * blurSpan * 2.2).rgb * 0.12;
  blur += texture2D(u_texture, waveUv - dir * blurSpan * 1.0).rgb * 0.2;
  blur += texture2D(u_texture, waveUv).rgb * 0.36;
  blur += texture2D(u_texture, waveUv + dir * blurSpan * 1.0).rgb * 0.2;
  blur += texture2D(u_texture, waveUv + dir * blurSpan * 2.2).rgb * 0.12;

  float chroma = (0.0007 + 0.0044 * u_intensity) * (0.4 + ring * 0.9);
  float r = texture2D(u_texture, waveUv + dir * chroma).r;
  float g = blur.g;
  float b = texture2D(u_texture, waveUv - dir * chroma).b;
  vec3 color = vec3(r, g, b);

  vec2 cell = floor((v_uv - center + vec2(0.5)) * vec2(180.0, 180.0));
  float dustNoise = rand(cell + floor(vec2(u_time * 16.0, u_time * 21.0)));
  float dust = step(0.992 - u_intensity * 0.04, dustNoise) * (0.18 + ring * 1.05);
  vec3 dustTint = mix(vec3(0.86, 0.9, 1.0), vec3(1.0, 0.86, 0.72), rand(cell * 0.07));
  color += dustTint * dust * (0.24 + u_intensity * 0.34);

  float flash = clamp((ring * 0.5 + ringTail * 0.32 + exp(-dist * 9.0) * 0.14) * (0.1 + u_intensity * 0.56), 0.0, 1.0);
  color = mix(color, vec3(1.0), flash * 0.32);
  gl_FragColor = vec4(color, texture2D(u_texture, waveUv).a);
}
${WEBGL_FOOTER}`,
    resolveUniforms: resolveUniformsBase,
  },
  {
    id: "gpu.impactShockwave",
    label: "Impact Shockwave",
    kind: "replace",
    webglFragment: `${WEBGL_HEADER}
void main() {
  vec2 center = vec2(clamp(u_progress, 0.0, 1.0), clamp(u_seed, 0.0, 1.0));
  vec2 dirRaw = v_uv - center;
  float dist = max(length(dirRaw), 0.0001);
  vec2 dir = dirRaw / dist;
  float amp = clamp(u_amp / 3.0, 0.0, 1.0);
  float beat = smoothstep(0.0, 1.0, sin(u_time * 9.0 * u_speed + u_progress * 7.0) * 0.5 + 0.5);

  float radiusA = 0.06 + fract(u_time * (0.26 + u_speed * 0.32)) * 0.88;
  float radiusB = clamp(radiusA - (0.06 + 0.05 * beat), 0.0, 1.0);
  float thickness = 0.012 + amp * 0.058;
  float ringA = exp(-pow((dist - radiusA) / max(thickness, 0.0001), 2.0));
  float ringB = exp(-pow((dist - radiusB) / max(thickness * 1.35, 0.0001), 2.0));

  float distortion = (ringA * 1.3 + ringB * 0.84) * (0.006 + u_intensity * 0.038) * (0.74 + beat * 0.62);
  vec2 uv = v_uv + dir * distortion;

  float chroma = (0.0008 + 0.005 * u_intensity) * (0.5 + ringA * 0.82);
  float rr = texture2D(u_texture, uv + dir * chroma).r;
  float gg = texture2D(u_texture, uv).g;
  float bb = texture2D(u_texture, uv - dir * chroma).b;
  vec3 color = vec3(rr, gg, bb);

  float flash = clamp((ringA * 0.54 + ringB * 0.36 + exp(-dist * 10.0) * 0.2) * (0.16 + u_intensity * 0.7), 0.0, 1.0);
  color = mix(color, vec3(1.0), flash * (0.26 + beat * 0.28));
  gl_FragColor = vec4(color, texture2D(u_texture, uv).a);
}
${WEBGL_FOOTER}`,
    resolveUniforms: resolveUniformsBase,
  },
];

const MODULE_BY_ID = new Map<FxGpuModuleId, FxGpuModule>(
  moduleDefs.map((module) => [module.id, module])
);

export function getFxGpuModule(id: string | null | undefined): FxGpuModule | null {
  if (!id) return null;
  return MODULE_BY_ID.get(id as FxGpuModuleId) ?? null;
}
